/**
 * HartSearchFilters - Search and filter controls for 5 entity types
 * (jobs, analysts, vendors, clients, facilities).
 * Each type has configurable search fields, toggle filters, and advanced filters.
 */
import { useState, useEffect, useRef } from 'react';
import { getIncludeAllLabel } from '../hartUtils';
import { US_STATES } from 'constants/states';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Switch,
  FormControlLabel,
  Grid,
  Chip,
  Button,
  Typography,
  Autocomplete,
} from "@mui/material";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';

// Constants
const SEARCH_DEBOUNCE_MS = 500;

// Unified filter configuration for all tables
export const TABLE_FILTER_CONFIG = {
  jobs: {
    searchFields: [
      { value: 'job_no', label: 'Job No' },
    ],
    defaultSearchField: 'job_no',
    toggleFilter: { key: 'includeAll', rowProperty: 'status', type: 'toggle' },
    advancedFilters: [
      { key: 'stDateFrom', label: 'Field Start From', rowProperty: 'start_date', type: 'date', comparison: '>=' },
      { key: 'stDateTo', label: 'Field Start To', rowProperty: 'start_date', type: 'date', comparison: '<=' },
      { key: 'enDateFrom', label: 'Field End From', rowProperty: 'end_date', type: 'date', comparison: '>=' },
      { key: 'enDateTo', label: 'Field End To', rowProperty: 'end_date', type: 'date', comparison: '<=' },
      { key: 'crDateFrom', label: 'Create From', rowProperty: 'created', type: 'date', comparison: '>=' },
      { key: 'crDateTo', label: 'Create To', rowProperty: 'created', type: 'date', comparison: '<=' },
      { key: 'clDateFrom', label: 'Close From', rowProperty: 'close_date', type: 'date', comparison: '>=' },
      { key: 'clDateTo', label: 'Close To', rowProperty: 'close_date', type: 'date', comparison: '<=' },
      { key: 'clients', label: 'Clients', rowProperty: 'clients', type: 'multiselect', comparison: 'in_array' },
      { key: 'hosts', label: 'Hosts', rowProperty: 'hosts', type: 'multiselect', comparison: 'in_array' },
      { key: 'recruiters', label: 'Recruiters', rowProperty: 'recruiters', type: 'multiselect', comparison: 'in_array' },
      { key: 'jobName', label: 'Job Name', rowProperty: 'job_name', type: 'text', comparison: 'contains' },
      { key: 'jobType', label: 'Job Type', rowProperty: 'job_type', type: 'dropdown', comparison: 'contains', endpoint: '/hart/job_types' },
      { key: 'jobCategory', label: 'Job Category', rowProperty: 'job_category', type: 'dropdown', comparison: 'contains', endpoint: '/hart/job_categories' },
      { key: 'jobStatus', label: 'Job Status', rowProperty: 'status', type: 'dropdown', comparison: 'contains', endpoint: '/hart/job_statuses' },
      { key: 'firewallStatus', label: 'Firewall', rowProperty: 'firewall_status', type: 'text', comparison: 'contains' },
      { key: 'releaseStatus', label: 'Release', rowProperty: 'release_status', type: 'dropdown', comparison: 'contains', endpoint: '/hart/release_statuses' },
      { key: 'abstract', label: 'Abstract', rowProperty: 'abstract', type: 'text', comparison: 'contains' },
    ],
  },
  analysts: {
    searchFields: [
      { value: 'name', label: 'Analyst Name' },
      { value: 'email', label: 'Email' },
    ],
    defaultSearchField: 'name',
    jobsToggleFilter: { key: 'includeInactiveJobs' },
    advancedFilters: [
      { key: 'stDateFrom', label: 'Field Start From', rowProperty: 'jobs', dateField: 'start_date', type: 'related_job_date', comparison: '>=' },
      { key: 'stDateTo', label: 'Field Start To', rowProperty: 'jobs', dateField: 'start_date', type: 'related_job_date', comparison: '<=' },
      { key: 'enDateFrom', label: 'Field End From', rowProperty: 'jobs', dateField: 'end_date', type: 'related_job_date', comparison: '>=' },
      { key: 'enDateTo', label: 'Field End To', rowProperty: 'jobs', dateField: 'end_date', type: 'related_job_date', comparison: '<=' },
      { key: 'crDateFrom', label: 'Job Create From', rowProperty: 'jobs', dateField: 'created', type: 'related_job_date', comparison: '>=' },
      { key: 'crDateTo', label: 'Job Create To', rowProperty: 'jobs', dateField: 'created', type: 'related_job_date', comparison: '<=' },
      { key: 'clDateFrom', label: 'Job Close From', rowProperty: 'jobs', dateField: 'close_date', type: 'related_job_date', comparison: '>=' },
      { key: 'clDateTo', label: 'Job Close To', rowProperty: 'jobs', dateField: 'close_date', type: 'related_job_date', comparison: '<=' },
    ],
  },
  vendors: {
    searchFields: [
      { value: 'name', label: 'Vendor Name' },
    ],
    defaultSearchField: 'name',
    toggleFilter: { key: 'includeAll', rowProperty: 'status', type: 'toggle' },
    jobsToggleFilter: { key: 'includeInactiveJobs' },
    advancedFilters: [
      { key: 'type', label: 'Vendor Type', rowProperty: 'type', type: 'dropdown', comparison: 'contains', endpoint: '/hart/vendor_types' },
      { key: 'stDateFrom', label: 'Field Start From', rowProperty: 'sessions', dateField: 'start_date', type: 'related_job_date', comparison: '>=' },
      { key: 'stDateTo', label: 'Field Start To', rowProperty: 'sessions', dateField: 'start_date', type: 'related_job_date', comparison: '<=' },
      { key: 'enDateFrom', label: 'Field End From', rowProperty: 'sessions', dateField: 'end_date', type: 'related_job_date', comparison: '>=' },
      { key: 'enDateTo', label: 'Field End To', rowProperty: 'sessions', dateField: 'end_date', type: 'related_job_date', comparison: '<=' },
      { key: 'crDateFrom', label: 'Job Create From', rowProperty: 'sessions', dateField: 'created', type: 'related_job_date', comparison: '>=' },
      { key: 'crDateTo', label: 'Job Create To', rowProperty: 'sessions', dateField: 'created', type: 'related_job_date', comparison: '<=' },
      { key: 'clDateFrom', label: 'Job Close From', rowProperty: 'sessions', dateField: 'close_date', type: 'related_job_date', comparison: '>=' },
      { key: 'clDateTo', label: 'Job Close To', rowProperty: 'sessions', dateField: 'close_date', type: 'related_job_date', comparison: '<=' },
      { key: 'city', label: 'City', rowProperty: 'city', type: 'text', comparison: 'contains' },
      { key: 'state', label: 'State', param: 'state', rowProperty: 'state', type: 'state_dropdown', comparison: 'equals' },
      { key: 'zip_code', label: 'Zip', rowProperty: 'zip_code', type: 'text', comparison: 'contains' },
    ],
  },
  clients: {
    searchFields: [
      { value: 'name', label: 'Org Name' },
    ],
    defaultSearchField: 'name',
    toggleFilter: { key: 'includeAll', rowProperty: 'status', type: 'toggle' },
    jobsToggleFilter: { key: 'includeInactiveJobs' },
    advancedFilters: [
      { key: 'sector', label: 'Org Sector', rowProperty: 'sector', type: 'dropdown', comparison: 'contains', endpoint: '/hart/industry_sectors' },
      { key: 'stDateFrom', label: 'Field Start From', rowProperty: 'jobs', dateField: 'start_date', type: 'related_job_date', comparison: '>=' },
      { key: 'stDateTo', label: 'Field Start To', rowProperty: 'jobs', dateField: 'start_date', type: 'related_job_date', comparison: '<=' },
      { key: 'enDateFrom', label: 'Field End From', rowProperty: 'jobs', dateField: 'end_date', type: 'related_job_date', comparison: '>=' },
      { key: 'enDateTo', label: 'Field End To', rowProperty: 'jobs', dateField: 'end_date', type: 'related_job_date', comparison: '<=' },
      { key: 'crDateFrom', label: 'Job Create From', rowProperty: 'jobs', dateField: 'created', type: 'related_job_date', comparison: '>=' },
      { key: 'crDateTo', label: 'Job Create To', rowProperty: 'jobs', dateField: 'created', type: 'related_job_date', comparison: '<=' },
      { key: 'clDateFrom', label: 'Job Close From', rowProperty: 'jobs', dateField: 'close_date', type: 'related_job_date', comparison: '>=' },
      { key: 'clDateTo', label: 'Job Close To', rowProperty: 'jobs', dateField: 'close_date', type: 'related_job_date', comparison: '<=' },
      { key: 'year_started', label: 'Year Started', rowProperty: 'year_started', type: 'text', comparison: 'contains' },
      { key: 'parent_client', label: 'Parent Organization', rowProperty: 'parent_client', type: 'text', comparison: 'contains' },
    ],
  },
  facilities: {
    searchFields: [
      { value: 'name', label: 'Facility Name' },
    ],
    defaultSearchField: 'name',
    toggleFilter: { key: 'includeAll', rowProperty: 'status', type: 'toggle' },
    jobsToggleFilter: { key: 'includeInactiveJobs' },
    advancedFilters: [
      { key: 'stDateFrom', label: 'Field Start From', rowProperty: 'sessions', dateField: 'start_date', type: 'related_job_date', comparison: '>=' },
      { key: 'stDateTo', label: 'Field Start To', rowProperty: 'sessions', dateField: 'start_date', type: 'related_job_date', comparison: '<=' },
      { key: 'enDateFrom', label: 'Field End From', rowProperty: 'sessions', dateField: 'end_date', type: 'related_job_date', comparison: '>=' },
      { key: 'enDateTo', label: 'Field End To', rowProperty: 'sessions', dateField: 'end_date', type: 'related_job_date', comparison: '<=' },
      { key: 'crDateFrom', label: 'Job Create From', rowProperty: 'sessions', dateField: 'created', type: 'related_job_date', comparison: '>=' },
      { key: 'crDateTo', label: 'Job Create To', rowProperty: 'sessions', dateField: 'created', type: 'related_job_date', comparison: '<=' },
      { key: 'clDateFrom', label: 'Job Close From', rowProperty: 'sessions', dateField: 'close_date', type: 'related_job_date', comparison: '>=' },
      { key: 'clDateTo', label: 'Job Close To', rowProperty: 'sessions', dateField: 'close_date', type: 'related_job_date', comparison: '<=' },
      { key: 'city', label: 'City', rowProperty: 'city', type: 'text', comparison: 'contains' },
      { key: 'state', label: 'State', param: 'state', rowProperty: 'state', type: 'state_dropdown', comparison: 'equals' },
      { key: 'zip_code', label: 'Zip', rowProperty: 'zip_code', type: 'text', comparison: 'contains' },
    ],
  },
};

// Helper functions that use the unified config
const getSearchFields = (tableType) => {
  return TABLE_FILTER_CONFIG[tableType]?.searchFields || [];
};

const getDefaultSearchField = (tableType) => {
  return TABLE_FILTER_CONFIG[tableType]?.defaultSearchField || 'name';
};

const getAdvancedFilters = (tableType) => {
  return TABLE_FILTER_CONFIG[tableType]?.advancedFilters || [];
};

// Create empty filters object from the config
const createEmptyFilters = (tableType) => {
  const config = TABLE_FILTER_CONFIG[tableType];
  if (!config) return {};

  const filters = {};
  // Add toggle filter if it exists
  if (config.toggleFilter) {
    filters[config.toggleFilter.key] = false;
  }
  // Add jobs toggle filter if it exists
  if (config.jobsToggleFilter) {
    filters[config.jobsToggleFilter.key] = false;
  }
  // Add all advanced filter fields if they exist
  if (config.advancedFilters && config.advancedFilters.length > 0) {
    config.advancedFilters.forEach(filter => {
      // Multi-select filters use arrays, others use empty strings
      filters[filter.key] = filter.type === 'multiselect' ? [] : '';
    });
  }
  return filters;
};

// Returns whether advanced filters should be shown for this table type
const hasAdvancedFilters = (tableType) => {
  const config = TABLE_FILTER_CONFIG[tableType];
  return config && config.advancedFilters && config.advancedFilters.length > 0;
};

export default function HartSearchFilters({
  tableType,
  onQueryChange,
}) {
  const [searchField, setSearchField] = useState(getDefaultSearchField(tableType));
  const [searchValue, setSearchValue] = useState([]);
  const [searchInputValue, setSearchInputValue] = useState(''); 
  const [advancedFilters, setAdvancedFilters] = useState(() => createEmptyFilters(tableType));
  
  const [debouncedSearchValue, setDebouncedSearchValue] = useState([]);
  const [debouncedAdvancedFilters, setDebouncedAdvancedFilters] = useState(advancedFilters);
  const isInitialMountRef = useRef(true);

  // State for multi-select dropdown options
  const [clientOptions, setClientOptions] = useState([]);
  const [hostOptions, setHostOptions] = useState([]);
  const [recruiterOptions, setRecruiterOptions] = useState([]);
  const [searchOptions, setSearchOptions] = useState([]);
  const [isLoadingOptions, setIsLoadingOptions] = useState(false);
  const [isLoadingSearchOptions, setIsLoadingSearchOptions] = useState(false);
  
  // State for dropdown filter options
  const [dropdownOptions, setDropdownOptions] = useState({});

  const searchFields = getSearchFields(tableType);
  const showAdvancedFilters = hasAdvancedFilters(tableType);
  const config = TABLE_FILTER_CONFIG[tableType];
  const toggleFilterKey = config?.toggleFilter?.key;
  const jobsToggleFilterKey = config?.jobsToggleFilter?.key;

  // Reset when table changes
  useEffect(() => {
    setSearchField(getDefaultSearchField(tableType));
    setSearchValue([]);
    setSearchInputValue('');  
    setSearchOptions([]);  
    const emptyFilters = createEmptyFilters(tableType);
    setAdvancedFilters(emptyFilters);
    setDebouncedSearchValue([]);
    setDebouncedAdvancedFilters(emptyFilters);
    isInitialMountRef.current = true;
  }, [tableType]);

  // Clear search options when search field changes
  useEffect(() => {
    setSearchOptions([]);
  }, [searchField]);

  // Fetch client, vendor options for multi-select dropdowns (jobs only)
  useEffect(() => {
    if (tableType !== 'jobs') return;

    const fetchOptions = async () => {
      setIsLoadingOptions(true);
      try {
        // Fetch all clients and vendors in parallel
        const [clientsResponse, vendorsResponse] = await Promise.all([
          fetch('/hart/clients?all=true', { credentials: 'include' }),
          fetch('/hart/vendors?all=true', { credentials: 'include' }),
        ]);

        if (!clientsResponse.ok) {
          throw new Error('Failed to fetch clients');
        }
        if (!vendorsResponse.ok) {
          throw new Error('Failed to fetch vendors');
        }

        const clientsData = await clientsResponse.json();
        const vendorsData = await vendorsResponse.json();

        const clients = Array.isArray(clientsData) ? clientsData : (clientsData.results || []);
        const vendors = Array.isArray(vendorsData) ? vendorsData : (vendorsData.results || []);

        // Format and sort clients
        const formattedClients = clients
          .map(c => ({ id: String(c.org_id), name: c.name }))
          .sort((a, b) => a.name.localeCompare(b.name));

        // Filter and format hosts
        const hosts = vendors
          .filter(v => v.type?.toLowerCase() === 'host')
          .map(v => ({ id: String(v.vendor_id), name: v.name }))
          .sort((a, b) => a.name.localeCompare(b.name));

        // Filter and format recruiters
        const recruiters = vendors
          .filter(v => v.type?.toLowerCase() === 'recruiter')
          .map(v => ({ id: String(v.vendor_id), name: v.name }))
          .sort((a, b) => a.name.localeCompare(b.name));

        setClientOptions(formattedClients);
        setHostOptions(hosts);
        setRecruiterOptions(recruiters);
      } catch (error) {
        console.error('Error fetching filter options:', error);
      } finally {
        setIsLoadingOptions(false);
      }
    };

    fetchOptions();
  }, [tableType]);
  
  // Fetch dropdown filter options
  useEffect(() => {
    const fetchDropdownOptions = async () => {
      const filters = getAdvancedFilters(tableType);
      const dropdownFilters = filters.filter(f => f.type === 'dropdown' && f.endpoint);
      
      if (dropdownFilters.length === 0) return;
      
      const optionsMap = {};
      
      for (const filter of dropdownFilters) {
        try {
          const response = await fetch(filter.endpoint, {
            credentials: 'include',
          });
          
          if (response.ok) {
            const data = await response.json();
            optionsMap[filter.key] = data || [];
          }
        } catch (error) {
          console.error(`Error fetching dropdown options for ${filter.key}:`, error);
          optionsMap[filter.key] = [];
        }
      }
      
      setDropdownOptions(optionsMap);
    };
    
    fetchDropdownOptions();
  }, [tableType]);

  // Lazy load search dropdown options only when dropdown is opened
  const searchOptionsCacheRef = useRef({});
  
  const fetchSearchOptions = async () => {
    // Check cache first
    const cacheKey = `${tableType}-${searchField}`;
    if (searchOptionsCacheRef.current[cacheKey]) {
      setSearchOptions(searchOptionsCacheRef.current[cacheKey]);
      return;
    }

    setIsLoadingSearchOptions(true);
    try {
      // Use the new efficient distinct values endpoint
      const response = await fetch(`/hart/distinct/${tableType}/${searchField}/`, {
        credentials: 'include',
      });
      
      if (!response.ok) {
        throw new Error(`Failed to fetch distinct values for ${searchField}`);
      }
      
      const uniqueValues = await response.json();
      
      // Cache the results
      searchOptionsCacheRef.current[cacheKey] = uniqueValues;
      setSearchOptions(uniqueValues);
    } catch (error) {
      console.error('Error fetching search options:', error);
      setSearchOptions([]);
    } finally {
      setIsLoadingSearchOptions(false);
    }
  };

  // Debounce search input
  useEffect(() => {
    if (isInitialMountRef.current) {
      isInitialMountRef.current = false;
      return;
    }
    const timer = setTimeout(() => setDebouncedSearchValue(searchValue), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchValue]);

  // Debounce text/date filters (but not on initial mount)
  // Watch all filter fields from config (excluding toggle filters which are handled separately)
  useEffect(() => {
    if (isInitialMountRef.current) return;
    const timer = setTimeout(() => {
      setDebouncedAdvancedFilters(prev => {
        const toggleValues = {
          ...(toggleFilterKey ? { [toggleFilterKey]: prev[toggleFilterKey] } : {}),
          ...(jobsToggleFilterKey ? { [jobsToggleFilterKey]: prev[jobsToggleFilterKey] } : {}),
        };
        const restFilters = Object.fromEntries(
          Object.entries(advancedFilters).filter(
            ([key]) => key !== toggleFilterKey && key !== jobsToggleFilterKey
          )
        );
        return {
          ...prev,
          ...restFilters,
          ...toggleValues, // Preserve toggle filters (updated separately)
        };
      });
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [
    tableType,
    toggleFilterKey,
    jobsToggleFilterKey,
    // Stringify to track all dynamic filter keys as one stable dependency
    JSON.stringify(
      Object.entries(advancedFilters)
        .filter(([key]) => key !== toggleFilterKey && key !== jobsToggleFilterKey)
        .sort(([a], [b]) => a.localeCompare(b))
        .reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {})
    ),
  ]);

  // Update toggle filter immediately (no debounce)
  useEffect(() => {
    if (isInitialMountRef.current || !toggleFilterKey) return;
    setDebouncedAdvancedFilters(prev => {
      if (prev[toggleFilterKey] === advancedFilters[toggleFilterKey]) {
        return prev;
      }
      return {
        ...prev,
        [toggleFilterKey]: advancedFilters[toggleFilterKey]
      };
    });
  }, [toggleFilterKey, advancedFilters[toggleFilterKey]]);

  // Update jobs toggle filter
  useEffect(() => {
    if (isInitialMountRef.current || !jobsToggleFilterKey) return;
    setDebouncedAdvancedFilters(prev => {
      if (prev[jobsToggleFilterKey] === advancedFilters[jobsToggleFilterKey]) {
        return prev;
      }
      return {
        ...prev,
        [jobsToggleFilterKey]: advancedFilters[jobsToggleFilterKey]
      };
    });
  }, [jobsToggleFilterKey, advancedFilters[jobsToggleFilterKey]]);

  // Build query object and notify parent whenever debounced values change
  useEffect(() => {
    // Reset initial mount flag after first query is built
    if (isInitialMountRef.current) {
      isInitialMountRef.current = false;
    }

    // Build a map of filter configs from the current table's advancedFilters
    const currentTableFilterConfigs = {};
    if (config?.advancedFilters) {
      config.advancedFilters.forEach(filter => {
        currentTableFilterConfigs[filter.key] = {
          rowProperty: filter.rowProperty,
          type: filter.type,
          comparison: filter.comparison,
          ...(filter.param && { param: filter.param }),
          ...(filter.dateField && { dateField: filter.dateField }),
        };
      });
    }

    const query = {
      search: Array.isArray(debouncedSearchValue) && debouncedSearchValue.length > 0 ? {
        field: searchField,
        value: debouncedSearchValue.join(','),
      } : null,
      filters: Object.keys(debouncedAdvancedFilters)
        .filter(key => {
          const value = debouncedAdvancedFilters[key];
          // Skip toggle filters (handled separately) and empty values
          if (key === toggleFilterKey || key === jobsToggleFilterKey) return false;
          // Handle arrays (multiselect)
          if (Array.isArray(value)) {
            return value.length > 0;
          }
          // Handle strings and other types
          return value && value !== '';
        })
        .map(key => {
          const filterConfig = currentTableFilterConfigs[key];
          if (!filterConfig) return null;
          return {
            key,
            value: debouncedAdvancedFilters[key],
            ...filterConfig,
          };
        })
        .filter(Boolean),
      toggleFilter: toggleFilterKey ? {
        key: toggleFilterKey,
        value: debouncedAdvancedFilters[toggleFilterKey] ?? false,
        ...config.toggleFilter,
      } : null,
      jobsToggleFilter: jobsToggleFilterKey ? {
        key: jobsToggleFilterKey,
        value: debouncedAdvancedFilters[jobsToggleFilterKey] ?? false,
      } : null,
    };

    onQueryChange(query);
  }, [debouncedSearchValue, searchField, debouncedAdvancedFilters, toggleFilterKey, jobsToggleFilterKey, config, onQueryChange, tableType]);

  const handleClearFilters = () => {
    setSearchValue([]);
    const emptyFilters = createEmptyFilters(tableType);
    setAdvancedFilters(emptyFilters);
    setDebouncedSearchValue([]);
    setDebouncedAdvancedFilters(emptyFilters);
  };

  // Build active filter chips
  const activeFilterChips = [];
  
  if (Array.isArray(searchValue) && searchValue.length > 0) {
    const searchFieldLabel = searchFields.find(f => f.value === searchField)?.label || searchField;
    activeFilterChips.push({
      key: 'search',
      label: `${searchFieldLabel}: ${searchValue.join(', ')}`,
      onDelete: () => setSearchValue([]),
    });
  }

  if (showAdvancedFilters && advancedFilters) {
    getAdvancedFilters(tableType).forEach(fieldDef => {
      const value = advancedFilters[fieldDef.key];
      
      // Handle multiselect arrays
      if (fieldDef.type === 'multiselect' && Array.isArray(value) && value.length > 0) {
        const names = value.map(v => v.name).join(', ');
        activeFilterChips.push({
          key: fieldDef.key,
          label: `${fieldDef.label}: ${names}`,
          onDelete: () => setAdvancedFilters({
            ...advancedFilters,
            [fieldDef.key]: [],
          }),
        });
      }
      // Handle other field types (text, date, etc.)
      else if (value && !Array.isArray(value)) {
        activeFilterChips.push({
          key: fieldDef.key,
          label: `${fieldDef.label}: ${value}`,
          onDelete: () => setAdvancedFilters({
            ...advancedFilters,
            [fieldDef.key]: '',
          }),
        });
      }
    });

    if (config?.toggleFilter && advancedFilters[config.toggleFilter.key]) {
      activeFilterChips.push({
        key: config.toggleFilter.key,
        label: getIncludeAllLabel(tableType),
        onDelete: () => setAdvancedFilters({
          ...advancedFilters,
          [config.toggleFilter.key]: false,
        }),
      });
    }

    if (config?.jobsToggleFilter && advancedFilters[config.jobsToggleFilter.key]) {
      activeFilterChips.push({
        key: config.jobsToggleFilter.key,
        label: 'Include All Jobs',
        onDelete: () => setAdvancedFilters({
          ...advancedFilters,
          [config.jobsToggleFilter.key]: false,
        }),
      });
    }
  }

  const hasActiveFilters = activeFilterChips.length > 0;

  return (
    <Card sx={{ mb: 2, overflow: 'visible' }}>
      <CardContent sx={{ pb: 1 }}>
        {/* Basic Search */}
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          {searchFields.length > 1 && (
            <Box sx={{ minWidth: '200px', flexShrink: 0 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Search Field</InputLabel>
                <Select
                  value={searchField}
                  label="Search Field"
                  onChange={(e) => {
                    setSearchField(e.target.value);
                    setSearchValue([]); // Clear search when changing fields
                  }}
                >
                  {searchFields.map(field => (
                    <MenuItem key={field.value} value={field.value}>
                      {field.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          )}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Autocomplete
              multiple
              freeSolo
              forcePopupIcon
              size="small"
              options={searchOptions}
              value={searchValue}
              inputValue={searchInputValue}
              onInputChange={(event, newInputValue) => {
                setSearchInputValue(newInputValue);
              }}
              onChange={(event, newValue) => {
                // Handle comma-separated input when Enter is pressed (but not for name fields)
                const isNameField = searchField.toLowerCase().includes('name');
                
                if (!isNameField && Array.isArray(newValue) && newValue.length > 0) {
                  const lastItem = newValue[newValue.length - 1];
                  if (typeof lastItem === 'string' && lastItem.includes(',')) {
                    const splitItems = lastItem.split(',').map(item => item.trim()).filter(Boolean);
                    const otherItems = newValue.slice(0, -1);
                    setSearchValue([...otherItems, ...splitItems]);
                    return;
                  }
                }
                setSearchValue(newValue);
              }}
              loading={isLoadingSearchOptions}
              onOpen={() => {
                // Lazy load options only when dropdown is opened
                if (searchOptions.length === 0 && !isLoadingSearchOptions) {
                  fetchSearchOptions();
                }
              }}
              renderInput={(params) => {
                // Disable comma-splitting for name fields (to preserve "Last, First" format)
                const isNameField = searchField.toLowerCase().includes('name');
                
                return (
                  <TextField
                    {...params}
                    label={searchFields.length === 1 ? `Search ${searchFields[0].label}` : "Search"}
                    placeholder={isNameField ? "Type + Enter or select to add multiple" : "Type + Enter, paste comma-separated, or select"}
                    onPaste={(event) => {
                      // Only handle comma-separated paste for non-name fields
                      if (!isNameField) {
                        const pastedText = event.clipboardData.getData('text');
                        if (pastedText.includes(',')) {
                          event.preventDefault();
                          const items = pastedText.split(',').map(item => item.trim()).filter(Boolean);
                          setSearchValue([...searchValue, ...items]);
                        }
                      }
                    }}
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <>
                          <SearchIcon sx={{ ml: 1, mr: 0.5, color: 'action.active' }} />
                          {params.InputProps.startAdornment}
                        </>
                      ),
                    }}
                  />
                );
              }}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    {...getTagProps({ index })}
                    key={index}
                    label={option}
                    size="small"
                  />
                ))
              }
            />
          </Box>
          {config?.toggleFilter && (
            <Box sx={{ flexShrink: 0 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={advancedFilters?.[config.toggleFilter.key] || false}
                    onChange={(e) => setAdvancedFilters({
                      ...advancedFilters,
                      [config.toggleFilter.key]: e.target.checked,
                    })}
                    size="small"
                  />
                }
                label={getIncludeAllLabel(tableType)}
                sx={{
                  mr: 0,
                  ml: 0,
                  '& .MuiFormControlLabel-label': {
                    fontSize: '0.875rem',
                  }
                }}
              />
            </Box>
          )}
          {config?.jobsToggleFilter && (
            <Box sx={{ flexShrink: 0 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={advancedFilters?.[config.jobsToggleFilter.key] || false}
                    onChange={(e) => setAdvancedFilters({
                      ...advancedFilters,
                      [config.jobsToggleFilter.key]: e.target.checked,
                    })}
                    size="small"
                  />
                }
                label="Include All Jobs"
                sx={{
                  mr: 0,
                  ml: 0,
                  '& .MuiFormControlLabel-label': {
                    fontSize: '0.875rem',
                  }
                }}
              />
            </Box>
          )}
        </Box>

        {/* Advanced Search Accordion */}
        {showAdvancedFilters && (
          <Accordion
            sx={{
              mt: 2,
              '&:before': { display: 'none' },
              boxShadow: 'none',
              border: '1px solid rgba(0, 0, 0, 0.12)',
              '&.Mui-expanded': {
                mt: 2,
              },
              '&:last-of-type': {
                borderBottomLeftRadius: '0px',
                borderBottomRightRadius: '0px',
              }
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon sx={{ color: 'primary.main' }} />}
              sx={{
                minHeight: '48px',
                '&.Mui-expanded': {
                  minHeight: '48px',
                },
                '& .MuiAccordionSummary-content': {
                  margin: '12px 0',
                  '&.Mui-expanded': {
                    margin: '12px 0',
                  },
                },
              }}
            >
              <Typography>Advanced Search</Typography>
            </AccordionSummary>
            <AccordionDetails
              sx={{
                padding: '16px',
              }}
            >
              <Grid container spacing={3}>
                {(() => {
                  const filters = getAdvancedFilters(tableType);
                  const dateRanges = {};
                  const textFields = [];
                  const multiselectFields = [];
                  const dropdownFields = [];
                  const stateDropdownFields = [];

                  // Group fields by type
                  filters.forEach(filter => {
                    if (filter.type === 'date' || filter.type === 'related_job_date') {
                      // For 'date', use rowProperty; for 'related_job_date', use dateField
                      const baseKey = filter.type === 'related_job_date' ? filter.dateField : filter.rowProperty;
                      if (!dateRanges[baseKey]) {
                        dateRanges[baseKey] = { from: null, to: null };
                      }
                      if (filter.comparison === '>=') {
                        dateRanges[baseKey].from = filter;
                      } else if (filter.comparison === '<=') {
                        dateRanges[baseKey].to = filter;
                      }
                    } else if (filter.type === 'text') {
                      textFields.push(filter);
                    } else if (filter.type === 'multiselect') {
                      multiselectFields.push(filter);
                    } else if (filter.type === 'dropdown') {
                      dropdownFields.push(filter);
                    } else if (filter.type === 'state_dropdown' || filter.type === 'related_state') {
                      stateDropdownFields.push(filter);
                    }
                  });

                  const dateRangeLabels = {
                    start_date: tableType === 'jobs' ? 'Field Start Date Range' : 'Job Field Start Date Range',
                    end_date: tableType === 'jobs' ? 'Field End Date Range' : 'Job Field End Date Range',
                    created: tableType === 'jobs' ? 'Create Date Range' : 'Job Create Date Range',
                    close_date: tableType === 'jobs' ? 'Close Date Range' : 'Job Close Date Range'
                  };

                  return (
                    <>
                      {/* Date Range Fields */}
                      {Object.entries(dateRanges).map(([rowProperty, range]) => (
                        <Grid item xs={12} md={6} key={rowProperty}>
                          <Typography variant="subtitle2" gutterBottom sx={{ mb: 1, fontWeight: 500 }}>
                            {dateRangeLabels[rowProperty] || `${rowProperty} Range`}
                          </Typography>
                          <Grid container spacing={2}>
                            {range.from && (
                              <Grid item xs={6}>
                                <TextField
                                  fullWidth
                                  size="small"
                                  label="From"
                                  type="date"
                                  value={advancedFilters?.[range.from.key] || ''}
                                  onChange={(e) => setAdvancedFilters({
                                    ...advancedFilters,
                                    [range.from.key]: e.target.value,
                                  })}
                                  InputLabelProps={{ shrink: true }}
                                />
                              </Grid>
                            )}
                            {range.to && (
                              <Grid item xs={6}>
                                <TextField
                                  fullWidth
                                  size="small"
                                  label="To"
                                  type="date"
                                  value={advancedFilters?.[range.to.key] || ''}
                                  onChange={(e) => setAdvancedFilters({
                                    ...advancedFilters,
                                    [range.to.key]: e.target.value,
                                  })}
                                  InputLabelProps={{ shrink: true }}
                                />
                              </Grid>
                            )}
                          </Grid>
                        </Grid>
                      ))}

                      {/* Multi-Select Fields */}
                      {multiselectFields.map((filter) => {
                        // Get options based on filter key
                        let options = [];
                        if (filter.key === 'clients') {
                          options = clientOptions;
                        } else if (filter.key === 'hosts') {
                          options = hostOptions;
                        } else if (filter.key === 'recruiters') {
                          options = recruiterOptions;
                        }

                        return (
                          <Grid item xs={12} md={6} key={filter.key}>
                            <Autocomplete
                              multiple
                              size="small"
                              options={options}
                              getOptionLabel={(option) => option.name}
                              value={advancedFilters?.[filter.key] || []}
                              onChange={(event, newValue) => {
                                setAdvancedFilters({
                                  ...advancedFilters,
                                  [filter.key]: newValue,
                                });
                              }}
                              loading={isLoadingOptions}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  label={filter.label}
                                  placeholder={`Select ${filter.label.toLowerCase()}`}
                                />
                              )}
                              renderTags={(value, getTagProps) =>
                                value.map((option, index) => (
                                  <Chip
                                    {...getTagProps({ index })}
                                    key={option.id}
                                    label={option.name}
                                    size="small"
                                  />
                                ))
                              }
                            />
                          </Grid>
                        );
                      })}

                      {/* Dropdown Fields */}
                      {dropdownFields.map((filter) => {
                        const options = dropdownOptions[filter.key] || [];

                        return (
                          <Grid item xs={12} md={6} key={filter.key}>
                            <FormControl fullWidth size="small">
                              <InputLabel>{filter.label}</InputLabel>
                              <Select
                                value={advancedFilters?.[filter.key] || ''}
                                label={filter.label}
                                onChange={(e) => setAdvancedFilters({
                                  ...advancedFilters,
                                  [filter.key]: e.target.value,
                                })}
                              >
                                <MenuItem value="">
                                  <em>All</em>
                                </MenuItem>
                                {options.map((option) => (
                                  <MenuItem key={option.name} value={option.name}>
                                    {option.name}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </Grid>
                        );
                      })}

                      {/* State Dropdown Fields */}
                      {stateDropdownFields.map((filter) => (
                        <Grid item xs={12} md={6} key={filter.key}>
                          <Autocomplete
                            size="small"
                            options={US_STATES}
                            getOptionLabel={(option) => {
                              if (typeof option === 'string') {
                                const match = US_STATES.find(s => s.abbr === option);
                                return match ? `${match.abbr} - ${match.name}` : option;
                              }
                              return `${option.abbr} - ${option.name}`;
                            }}
                            value={
                              advancedFilters?.[filter.key]
                                ? (US_STATES.find(s => s.abbr === advancedFilters[filter.key] || s.name === advancedFilters[filter.key]) || null)
                                : null
                            }
                            onChange={(event, newValue) => {
                              // Store abbr so backend and URL stay consistent
                              setAdvancedFilters({
                                ...advancedFilters,
                                [filter.key]: newValue ? (typeof newValue === 'object' ? newValue.abbr : newValue) : '',
                              });
                            }}
                            isOptionEqualToValue={(option, value) => option.abbr === value?.abbr}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label={filter.label}
                                placeholder="Select state"
                              />
                            )}
                          />
                        </Grid>
                      ))}

                      {/* Text Fields */}
                      {textFields.map((filter) => (
                        <Grid item xs={12} md={6} key={filter.key}>
                          <TextField
                            fullWidth
                            size="small"
                            label={filter.label}
                            placeholder="Text search (contains)"
                            value={advancedFilters?.[filter.key] || ''}
                            onChange={(e) => setAdvancedFilters({
                              ...advancedFilters,
                              [filter.key]: e.target.value,
                            })}
                            InputLabelProps={{ shrink: true }}
                          />
                        </Grid>
                      ))}
                    </>
                  );
                })()}
              </Grid>
            </AccordionDetails>
          </Accordion>
        )}
      </CardContent>

      {/* Active Filters Display */}
      {hasActiveFilters && (
        <Card sx={{ mb: 0, mt: -1.5, pt: 0, pb: 0, overflow: 'visible' }}>
          <CardContent
            sx={{
              mb: 0,
              mt: 0,
              pt: 1.5,
              pb: 0,
              '&:last-child': {
                pb: 1.5,
              }
            }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 3 }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0 }}>
                <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, fontSize: '0.75rem' }}>
                  Active Filters:
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {activeFilterChips.map(chip => (
                    <Chip
                      key={chip.key}
                      size="small"
                      label={chip.label}
                      onDelete={chip.onDelete}
                      sx={{
                        '&.MuiChip-deletable .MuiChip-deleteIcon': {
                          color: 'primary.main',
                          '&:hover': {
                            color: 'primary.dark',
                          }
                        }
                      }}
                    />
                  ))}
                </Box>
              </Box>
              <Button
                variant="outlined"
                startIcon={<ClearIcon />}
                onClick={handleClearFilters}
                sx={{ flexShrink: 0 }}
              >
                Clear All
              </Button>
            </Box>
          </CardContent>
        </Card>
      )}
    </Card>
  );
}
